import { ReactNode, useEffect, useState } from 'react';
import clsx from 'classnames';
import { makeStyles } from '@material-ui/core';
import { TooltipProps } from '@material-ui/core/Tooltip';
import { MDSTheme, MDSTooltip } from '@marqvision/mds/core';
import { MDSIcon, MDSButton, MDSTypography, MDSTypographyProps } from '@marqvision/mds-v2';

type Props = {
  url: string;
  icon?:
    | {
        size?: number;
      }
    | ReactNode;
  disabled?: boolean;
  onOpenLink?: () => void;
  tooltip?: {
    title: string;
    placement?: TooltipProps['placement'];
  };
};

const useOpenUrl = (params: Props) => {
  // !!리뷰포인트 - @elie
  // props.url 등을 사용하면 명시적이긴 하나 object destructuring 의 장점을 살리지 못함.. 고민 고민
  const [disabled, setDisabled] = useState<boolean>(false);
  const tooltip = Object.assign({ title: 'Open in new tab', placement: 'top' }, params.tooltip);
  const handleOpenLink = (url: string) => {
    if (params.onOpenLink) {
      params.onOpenLink();
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    setDisabled(params.disabled !== undefined ? params.disabled : !params.url);
  }, [params.url, params.disabled]);

  return {
    disabled,
    tooltip,
    handleOpenLink,
  };
};

const useOpenUrlFromTextStyles = makeStyles(({ color }) => ({
  wrapper: {
    cursor: 'pointer',
    '&:hover': {
      '& $copyIcon': {
        display: 'inline-flex',
      },
      '& $text': {
        ...MDSTheme.typography.medium,
        color: color.content.neutral.default.hover,
      },
    },
    display: 'flex',
    alignItems: 'end',
  },
  text: {},
  copyIcon: {
    marginLeft: '5px',
    flexShrink: 0,
    display: 'none',
    '&.tooLongText': {
      padding: '0 2px 2px 0',
    },
  },
}));

type OpenUrlFromTextProps = Props & {
  children: string;
} & MDSTypographyProps;

const TEXT_ELLIPSIS_BOUNDARY = 67;
export const OpenUrlFromText = (props: OpenUrlFromTextProps) => {
  const classes = useOpenUrlFromTextStyles();
  const { children, url, ...mdsTypographyProps } = props;

  const isEllipsis = TEXT_ELLIPSIS_BOUNDARY < children.length;

  const handleOpenLink = () => {
    if (props.onOpenLink) {
      props.onOpenLink();
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={classes.wrapper} onClick={handleOpenLink}>
      <MDSTypography {...mdsTypographyProps} className={classes.text}>
        {children}
        {!isEllipsis && (
          <MDSIcon.OpenNew size={16} color="color/content/neutral/default/hover" className={classes.copyIcon} />
        )}
      </MDSTypography>
      {isEllipsis && (
        <MDSIcon.OpenNew
          size={16}
          color="color/content/neutral/default/hover"
          className={clsx(classes.copyIcon, 'tooLongText')}
        />
      )}
    </div>
  );
};

// MDS v2 Chip을 이용한 새 디자인에 대응하기 위해 생성
export const OpenUrlChip = (props: Props) => {
  const { disabled, tooltip, handleOpenLink } = useOpenUrl(props);

  return (
    <>
      {!disabled ? (
        <MDSTooltip title={tooltip.title} placement={tooltip.placement}>
          <MDSButton
            color="bluegray"
            size="medium"
            variant="border"
            onClick={() => handleOpenLink(props.url)}
            icon={
              props.icon ? (
                <>{props.icon}</>
              ) : (
                <MDSIcon.OpenNew color={'color/content/neutral/default/normal'} size={16} />
              )
            }
          />
        </MDSTooltip>
      ) : (
        <MDSButton
          color="bluegray"
          size="medium"
          variant="border"
          isDisabled
          icon={<MDSIcon.OpenNew color={'color/content/neutral/default/disabled'} size={16} />}
        />
      )}
    </>
  );
};
